/**
 * Shared wizard step field primitives.
 *
 * These are the accessible control builders the refinance steps introduced in
 * Wave 5. WI-TV-W6-001 lifts them out of `refinance-steps.ts` unchanged so the
 * purchase steps render from exactly the same primitives — same markup, same
 * labelling, same autofocus marker, same value binding — instead of growing a
 * second visual and accessibility contract for the purchase journey.
 */
import type { SelectOption } from "../models/referral";
import type { WizardValues } from "./wizard-machine";

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function labelHtml(key: string, label: string, required: boolean): string {
  const marker = required ? ' <span class="wizard-req" aria-hidden="true">*</span>' : "";
  const optional = required ? "" : ' <span class="wizard-optional">(optional)</span>';
  return `<label class="wizard-label" for="${key}">${escapeHtml(label)}${marker}${optional}</label>`;
}

export function textFieldHtml(
  key: string,
  label: string,
  value: string,
  options: {
    required?: boolean;
    inputmode?: string;
    maxlength?: number;
    type?: string;
    /**
     * Standard autocomplete token for the field. Defaults to `"off"`, which is
     * what the property address fields use: they are manual entry only, with no
     * lookup or suggestion source. The standard borrower contact fields pass
     * their own token so native browser autofill keeps working.
     */
    autocomplete?: string;
    /** Placeholder shown when empty, e.g. the phone display mask. */
    placeholder?: string;
    /** Id of standing helper text describing the field's format/requirement. */
    describedBy?: string;
  } = {},
): string {
  const attrs = [
    `id="${key}"`,
    `name="${key}"`,
    `type="${options.type ?? "text"}"`,
    `autocomplete="${options.autocomplete ?? "off"}"`,
    options.inputmode ? `inputmode="${options.inputmode}"` : "",
    options.maxlength ? `maxlength="${options.maxlength}"` : "",
    options.placeholder ? `placeholder="${escapeHtml(options.placeholder)}"` : "",
    options.describedBy ? `aria-describedby="${options.describedBy}"` : "",
    `value="${escapeHtml(value)}"`,
  ]
    .filter(Boolean)
    .join(" ");
  return `
    <div class="wizard-field">
      ${labelHtml(key, label, options.required === true)}
      <input class="wizard-input" ${attrs} />
    </div>`;
}

export function selectFieldHtml(
  key: string,
  label: string,
  placeholder: string,
  optionList: readonly SelectOption[],
  selected: string,
  options: {
    /** Defaults to true: every select the wizard rendered before was required. */
    required?: boolean;
    /**
     * Standard autocomplete token. Defaults to `"off"`, which is what the
     * property selects use; an address select that native autofill should be
     * able to complete passes its own (optionally sectioned) token.
     */
    autocomplete?: string;
  } = {},
): string {
  const optionsHtml = [`<option value="">${escapeHtml(placeholder)}</option>`]
    .concat(
      optionList.map(
        (o) =>
          `<option value="${escapeHtml(o.value)}"${o.value === selected ? " selected" : ""}>${escapeHtml(o.label)}</option>`,
      ),
    )
    .join("");
  return `
    <div class="wizard-field">
      ${labelHtml(key, label, options.required !== false)}
      <select class="wizard-select" id="${key}" name="${key}" autocomplete="${options.autocomplete ?? "off"}">${optionsHtml}</select>
    </div>`;
}

/**
 * Event a step body dispatches when the borrower asked to remove the optional
 * branch it belongs to, so the steps that branch owns no longer apply.
 *
 * The step owns *what* to clear and the shell owns *whether* the removal happens
 * at all: the shell runs `detail.clear` and then settles the navigation
 * consequence — pruning what is now unreachable, relocating once if the
 * displayed step ceased to exist, and re-rendering. A shell that refuses the
 * removal (an attempt is in flight, or the referral was accepted) simply never
 * runs the clear, so a refused removal leaves every answer exactly as it was.
 * Nothing is validated, skipped, or submitted, which keeps a removal free of
 * submission semantics.
 */
export const WIZARD_STEP_RESET_EVENT = "wizard:step-reset";

/** Payload of {@link WIZARD_STEP_RESET_EVENT}. */
export interface WizardStepResetDetail {
  /** Clears the values the removed branch owns. Run only once accepted. */
  readonly clear: () => void;
}

/** Asks the shell to remove the optional branch the step body belongs to. */
export function dispatchStepReset(target: HTMLElement, clear: () => void): void {
  target.dispatchEvent(
    new CustomEvent<WizardStepResetDetail>(WIZARD_STEP_RESET_EVENT, {
      bubbles: true,
      detail: { clear },
    }),
  );
}

/**
 * Renders an accessible single-select radio group. The group is labelled by its
 * legend, and the first control carries the step autofocus marker.
 */
export function radioGroupHtml(
  key: string,
  legend: string,
  optionList: readonly SelectOption[],
  selected: string,
  options: { autofocus?: boolean; describedBy?: string; labelledBy?: string } = {},
): string {
  const described = options.describedBy ? ` aria-describedby="${options.describedBy}"` : "";
  // A group whose question is already on the page is labelled by that text
  // rather than repeating it in a legend.
  const labelled = options.labelledBy ? ` aria-labelledby="${options.labelledBy}"` : "";
  const legendHtml = options.labelledBy
    ? ""
    : `<legend class="visually-hidden">${escapeHtml(legend)}</legend>`;
  return `
    <fieldset class="wizard-choice-group"${labelled}${described}>
      ${legendHtml}
      ${optionList
        .map((option, i) => {
          const id = `${key}-${option.value.replace(/[^A-Za-z0-9]+/g, "-")}`;
          return `
        <label class="wizard-choice" for="${id}">
          <input
            type="radio"
            id="${id}"
            name="${key}"
            value="${escapeHtml(option.value)}"
            ${option.value === selected ? "checked" : ""}
            ${options.autofocus === true && i === 0 ? 'data-autofocus="true"' : ""}
          />
          <span class="wizard-choice-label">${escapeHtml(option.label)}</span>
        </label>`;
        })
        .join("")}
    </fieldset>`;
}

/** Binds a radio group to the value store, invoking `onChange` after each pick. */
export function bindRadioGroup(
  container: HTMLElement,
  key: string,
  values: WizardValues,
  onChange?: (value: string) => void,
): void {
  container.querySelectorAll<HTMLInputElement>(`input[name="${key}"]`).forEach((input) => {
    input.addEventListener("change", () => {
      if (!input.checked) return;
      values.set(key, input.value);
      onChange?.(input.value);
    });
  });
}

/** Binds every named text/select control in `container` to the value store. */
export function bindValueInputs(container: HTMLElement, values: WizardValues): void {
  container
    .querySelectorAll<HTMLInputElement | HTMLSelectElement>("input[name], select[name]")
    .forEach((control) => {
      const key = control.name;
      const handler = (): void => values.set(key, control.value);
      control.addEventListener("input", handler);
      control.addEventListener("change", handler);
    });
}

/** The exact US phone display mask every wizard phone field renders toward. */
export const PHONE_DISPLAY_MASK = "(000) 000-0000";

/** Keeps only the digits of `value`. */
function digitsOf(value: string): string {
  return value.replace(/\D/g, "");
}

/**
 * Renders up to ten canonical digits as `(000) 000-0000`, growing the mask only
 * as far as the digits present so a partial entry never shows empty groups.
 */
export function formatPhoneDisplay(value: string): string {
  const d = digitsOf(value).slice(0, 10);
  if (d.length === 0) return "";
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/**
 * Returns the caret index in `formatted` that has exactly `digitsLeft` canonical
 * digits to its left, so the caret stays anchored to the digit the borrower was
 * editing across insertion, deletion, paste, and reformatting.
 */
function caretForDigitsLeft(formatted: string, digitsLeft: number): number {
  if (digitsLeft <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < formatted.length; i += 1) {
    if (/\d/.test(formatted[i])) {
      seen += 1;
      if (seen === digitsLeft) return i + 1;
    }
  }
  return formatted.length;
}

/** Number of canonical digits (capped at ten) to the left of `index` in `raw`. */
function digitsLeftOf(raw: string, index: number): number {
  return Math.min(digitsOf(raw.slice(0, index)).length, 10);
}

/**
 * Binds a phone input to dynamic `(000) 000-0000` display while storing exactly
 * the canonical digits (truncated to ten) in the value store.
 *
 * Input and paste sanitize to digits and truncate to ten; both selection
 * boundaries (start and end) and the selection direction are captured before
 * formatting and restored afterward by counting the canonical digits to the
 * left of each boundary, so a non-collapsed range is never collapsed and the
 * caret stays anchored to the digit the borrower was editing across insertion,
 * deletion, paste, and composition completion. Formatting is suspended for the
 * duration of an IME composition and applied once on `compositionend`, so a
 * composed sequence is never rewritten mid-composition.
 *
 * A `change` handler mirrors the `input` path so a value populated only through
 * a `change` event — as some browsers and password managers autofill — is still
 * sanitized, truncated, formatted, and committed to the canonical ten-digit
 * store, keeping live Continue validity correct on the autofill path too.
 */
export function bindPhoneInput(
  container: HTMLElement,
  key: string,
  values: WizardValues,
): void {
  const input = container.querySelector<HTMLInputElement>(`input[name="${key}"]`);
  if (!input) return;

  // Canonicalize whatever was seeded (digits, or a formatted prefill) so the
  // store always holds exactly the ten-digit payload from the first render.
  values.set(key, digitsOf(values.get(key)).slice(0, 10));

  let composing = false;

  const reformat = (): void => {
    const raw = input.value;
    // Capture both selection boundaries and the direction as canonical-digit
    // positions, so restoring after formatting preserves the whole range.
    const startIndex = input.selectionStart ?? raw.length;
    const endIndex = input.selectionEnd ?? startIndex;
    const direction = input.selectionDirection ?? "none";
    const startDigits = digitsLeftOf(raw, startIndex);
    const endDigits = digitsLeftOf(raw, endIndex);

    const canonical = digitsOf(raw).slice(0, 10);
    const formatted = formatPhoneDisplay(canonical);
    values.set(key, canonical);
    if (input.value !== formatted) input.value = formatted;

    const start = caretForDigitsLeft(formatted, startDigits);
    const end = caretForDigitsLeft(formatted, endDigits);
    try {
      input.setSelectionRange(start, end, direction);
    } catch {
      // Some input types disallow selection APIs; the value is already correct.
    }
  };

  input.addEventListener("compositionstart", () => {
    composing = true;
  });
  input.addEventListener("compositionend", () => {
    composing = false;
    reformat();
  });
  input.addEventListener("input", () => {
    // A composed sequence is left untouched until it commits on compositionend.
    if (composing) {
      values.set(key, digitsOf(input.value).slice(0, 10));
      return;
    }
    reformat();
  });
  input.addEventListener("change", () => {
    // A `change` that lands mid-composition is left to `compositionend`; any
    // other change (including an autofill that emitted no `input`) is sanitized,
    // formatted, and committed exactly like a keyed edit.
    if (composing) return;
    reformat();
  });
}
